
/**
 * Module dependencies.
 */

import { Download } from 'src/types/downloads';
import { color, units } from 'src/styles/utils';
import { colors } from 'src/styles/colors';
import { theme } from 'styled-tools';
import { useTranslate } from 'src/core/utils/translator';
import CardTitle from 'src/components/core/typography/card-title';
import IconButton from 'src/components/core/buttons/icon-button';
import Image, { ImageProps } from 'src/components/core/image';
import React, { ReactElement } from 'react';
import Svg from 'src/components/core/svg';
import downloadIcon from 'src/assets/svg/download.svg';
import imageIcon from 'src/assets/svg/image.svg';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = Pick<Download, 'downloadUrl' | 'imageUrl' | 'name'>;

/**
 * `StyledImage` styled component.
 */

const StyledImage = styled(Image)<ImageProps>`
  transition: transform 5s ease-in;
`;

/**
 * `Card` styled component.
 *
 * Figma: imagem no topo; bloco inferior com fundo #eeebe9 (`beige100`).
 */

const Card = styled.div`
  background-color: ${color('white')};
  box-shadow: 0 2px 12px rgba(17, 17, 17, 0.06);
  color: ${color('textColor')};
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  transition: color ${theme('animations.defaultTransition')};

  &:focus,
  &:hover {
    color: ${color('brown400')};

    ${StyledImage} {
      transform: scale(1.5);
    }
  }
`;

/**
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled.div`
  flex-shrink: 0;
  height: ${units(25)};
  overflow: hidden;
  position: relative;
`;

/**
 * `ImagePlaceholder` styled component.
 */

const ImagePlaceholder = styled.div`
  align-items: center;
  background-color: ${color('grey100')};
  display: flex;
  flex-shrink: 0;
  height: ${units(25)};
  justify-content: center;
`;

/**
 * `InfoWrapper` styled component.
 */

const InfoWrapper = styled.div`
  align-items: flex-start;
  background-color: ${color('beige100')};
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: ${units(21)};
  min-width: 0;
  padding: ${units(3)};
  width: 100%;
`;

/**
 * `TitleBlock` styled component.
 */

const TitleBlock = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  overflow-wrap: anywhere;
  width: 100%;
`;

/**
 * `DownloadAction` styled component.
 */

const DownloadAction = styled.div`
  flex-shrink: 0;
  margin-top: auto;
  padding-top: ${units(2)};
  width: 100%;
`;

/**
 * `DownloadCard` component.
 */

const DownloadCard = (props: Props): ReactElement => {
  const { downloadUrl, imageUrl, name } = props;
  const { translate } = useTranslate();

  return (
    <Card>
      {imageUrl ? (
        <ImageWrapper>
          <StyledImage
            alt={name}
            layout={'fill'}
            objectFit={'cover'}
            src={imageUrl}
          />
        </ImageWrapper>
      ) : (
        <ImagePlaceholder>
          <Svg
            color={colors.brown200}
            icon={imageIcon}
            size={units(8)}
          />
        </ImagePlaceholder>
      )}

      <InfoWrapper>
        <TitleBlock>
          <CardTitle size={'small'}>
            {name}
          </CardTitle>
        </TitleBlock>

        <DownloadAction>
          <IconButton
            aria-label={'Download'}
            icon={downloadIcon}
            iconSize={units(4)}
            label={translate('common:cardActions.download')}
            onClick={() => {
              const win = window.open(downloadUrl, '_blank');

              if (win) {
                win.opener = null;
              }
            }}
          />
        </DownloadAction>
      </InfoWrapper>
    </Card>
  );
};

/**
 * Export `DownloadCard` component.
 */

export default DownloadCard;
